import { type GiftTypeDto, isVipActive, type LiveChatDto, type LiveJoinDto } from '@spin/shared';
import {
  createLocalTracks,
  type LocalVideoTrack,
  type RemoteTrack,
  Room,
  RoomEvent,
  type Track,
  Track as TrackKind,
  VideoPresets,
} from 'livekit-client';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { game } from '../api/game';
import { ApiError, clientId, http } from '../api/http';
import { type LiveEvent, liveHandoff } from '../api/live';
import { Avatar, PlayerName, Sheet, Spinner } from '../components/common';
import { Emoji } from '../components/Emoji';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { loadGifts } from '../table/sheets';
import { tg } from '../tg/bridge';

type Status = 'connecting' | 'live' | 'ended' | 'failed';
type Line = LiveChatDto & { gift?: boolean };
type Floater = { key: number; emoji: string; big: boolean; x: number };

let floaterKey = 0;

function GiftPicker({
  name,
  onSend,
  onClose,
}: {
  name: string;
  onSend: (g: GiftTypeDto) => void;
  onClose: () => void;
}) {
  const t = useT();
  const balance = useSession((s) => s.me?.balance ?? 0);
  const vip = useSession((s) => isVipActive(s.me?.vipUntil ?? null));
  const [gifts, setGifts] = useState<GiftTypeDto[] | null>(null);
  useEffect(() => {
    loadGifts()
      .then((all) => setGifts(all.filter((g) => g.effect !== 'song')))
      .catch(reportError);
  }, []);
  return (
    <Sheet onClose={onClose}>
      <b>🎁 {t('live.gift_title', { name })}</b>
      {!gifts ? (
        <Spinner />
      ) : (
        <div className="gift-grid live-gifts">
          {gifts.map((g) => (
            <button
              key={g.id}
              className="gift"
              disabled={g.price > balance || (g.vipOnly && !vip)}
              onClick={() => onSend(g)}
              title={g.name}
              data-testid={`live-gift-${g.code}`}
            >
              <span className="emoji">
                <Emoji e={g.emoji} />
              </span>
              <span className="price">{g.vipOnly && !vip ? '🔒 VIP' : `❤ ${g.price}`}</span>
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}

/** One stream: the video full screen, chat and reactions over it; the streamer also gets camera controls. */
export function LiveRoom({ id }: { id: string }) {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const back = useSession((s) => s.back);
  const toast = useUi((s) => s.toast);
  const [join, setJoin] = useState<LiveJoinDto | null>(null);
  const [status, setStatus] = useState<Status>('connecting');
  /** Why the video could not connect (shown small under the message, so testers can report it). */
  const [failure, setFailure] = useState<string | null>(null);
  /** Some Telegram apps (e.g. Telegram Desktop on Linux) open Mini Apps in a view without WebRTC. */
  const noWebrtc = typeof RTCPeerConnection === 'undefined';
  const [endReason, setEndReason] = useState<string>('ended');
  const [video, setVideo] = useState<Track | null>(null);
  const [needsTap, setNeedsTap] = useState(false);
  const [viewers, setViewers] = useState(0);
  const [likes, setLikes] = useState(0);
  const [lines, setLines] = useState<Line[]>([]);
  const [text, setText] = useState('');
  const [floaters, setFloaters] = useState<Floater[]>([]);
  const [giftOpen, setGiftOpen] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const room = useRef<Room | null>(null);
  const videoEl = useRef<HTMLVideoElement>(null);
  const audioHost = useRef<HTMLDivElement>(null);
  const linesEl = useRef<HTMLDivElement>(null);
  const streamer = join?.role === 'streamer';

  const float = (emoji: string, big = false) => {
    const key = ++floaterKey;
    setFloaters((all) => [...all.slice(-24), { key, emoji, big, x: Math.random() }]);
    window.setTimeout(() => setFloaters((all) => all.filter((f) => f.key !== key)), big ? 3_000 : 2_200);
  };

  // Media: connect to the LiveKit room; the streamer publishes the camera and microphone.
  useEffect(() => {
    if (noWebrtc) return;
    let cancelled = false;
    const timers: number[] = [];
    const r = new Room({ adaptiveStream: true, dynacast: true });
    room.current = r;
    r.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind === TrackKind.Kind.Video) setVideo(track);
      else if (track.kind === TrackKind.Kind.Audio) {
        const el = track.attach();
        el.hidden = true;
        audioHost.current?.appendChild(el);
      }
    });
    r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach().forEach((el) => el.remove());
      if (track.kind === TrackKind.Kind.Video) setVideo(null);
    });
    r.on(RoomEvent.AudioPlaybackStatusChanged, () => setNeedsTap(!r.canPlaybackAudio));

    (async () => {
      const j = liveHandoff.take(id) ?? (await http.post<LiveJoinDto>(`/live/${id}/watch`));
      if (cancelled) return;
      setJoin(j);
      setViewers(j.stream.viewers);
      setLikes(j.stream.likes);
      await r.connect(j.url, j.token);
      if (cancelled) return;
      if (j.role === 'streamer') {
        const tracks = await createLocalTracks({
          audio: { echoCancellation: true, noiseSuppression: true },
          video: { facingMode: 'user', resolution: VideoPresets.h720.resolution },
        });
        if (cancelled) {
          tracks.forEach((track) => track.stop());
          return;
        }
        for (const track of tracks) await r.localParticipant.publishTrack(track);
        const camera = tracks.find((track) => track.kind === TrackKind.Kind.Video);
        if (camera) setVideo(camera);
        const ping = () => void game.emit('live.ping', { streamId: id }).catch(() => undefined);
        ping();
        timers.push(window.setInterval(ping, 10_000));
      }
      const enter = () =>
        void game
          .emit<'live.join', { viewers: number; likes: number }>('live.join', { streamId: id })
          .then((res) => {
            setViewers(res.viewers);
            setLikes(res.likes);
          })
          .catch(() => undefined);
      enter();
      // Repeated so the viewer count knows who is still here.
      timers.push(window.setInterval(enter, 20_000));
      setNeedsTap(!r.canPlaybackAudio);
      setStatus('live');
    })().catch((err: unknown) => {
      if (cancelled) return;
      if (err instanceof ApiError && err.code === 'STREAM_ENDED') {
        setStatus('ended');
        return;
      }
      setStatus('failed');
      if (err instanceof ApiError) reportError(err);
      else setFailure(String((err as Error)?.message ?? err).slice(0, 160));
    });

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearInterval(timer));
      void game.emit('live.leave', { streamId: id }).catch(() => undefined);
      void r.disconnect();
      room.current = null;
    };
  }, [id, noWebrtc]);

  useEffect(() => {
    const el = videoEl.current;
    if (!video || !el) return;
    video.attach(el);
    return () => {
      video.detach(el);
    };
  }, [video]);

  // The audience side: chat, likes, gifts, counts and the end of the stream.
  useEffect(() => {
    const onEvent = (e: Event) => {
      const ev = (e as CustomEvent<LiveEvent>).detail;
      if (ev.data.streamId !== id) return;
      if (ev.type === 'chat') setLines((all) => [...all.slice(-40), ev.data]);
      else if (ev.type === 'viewers') setViewers(ev.data.viewers);
      else if (ev.type === 'like') {
        setLikes(ev.data.likes);
        if (ev.data.from !== me.id) float('❤️');
      } else if (ev.type === 'gift') {
        float(ev.data.emoji, true);
        const line: Line = {
          id: `gift-${++floaterKey}`,
          streamId: id,
          from: { id: ev.data.from.id, name: ev.data.from.name, vip: false },
          text: t('live.sent_gift', { name: ev.data.from.name, emoji: ev.data.emoji }),
          at: new Date().toISOString(),
          gift: true,
        };
        setLines((all) => [...all.slice(-40), line]);
      } else if (ev.type === 'ended') {
        setEndReason(ev.data.reason);
        setStatus('ended');
        void room.current?.disconnect();
      }
    };
    window.addEventListener('spin:live', onEvent);
    return () => window.removeEventListener('spin:live', onEvent);
  }, [id, me.id, t]);

  useEffect(() => {
    const el = linesEl.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setText('');
    try {
      await game.emit('live.chat', { streamId: id, text: body, clientMsgId: clientId() });
    } catch (err) {
      setText(body);
      reportError(err);
    }
  };

  const like = () => {
    float('❤️');
    tg.haptic('light');
    void game.emit('live.like', { streamId: id }).catch(() => undefined);
  };

  const gift = async (g: GiftTypeDto) => {
    setGiftOpen(false);
    try {
      await http.post(`/live/${id}/gift`, { giftTypeId: g.id, clientTxId: clientId() });
      tg.haptic('success');
    } catch (err) {
      reportError(err);
    }
  };

  const report = async () => {
    try {
      await http.post(`/live/${id}/report`, {});
      toast(t('live.reported'), 'success');
    } catch (err) {
      reportError(err);
    }
  };

  const end = async () => {
    setConfirmEnd(false);
    try {
      await http.post(`/live/${id}/end`);
    } catch (err) {
      reportError(err);
    }
    back();
  };

  const toggleMic = async () => {
    await room.current?.localParticipant.setMicrophoneEnabled(!micOn);
    setMicOn(!micOn);
  };

  const switchCamera = async () => {
    const next = facing === 'user' ? 'environment' : 'user';
    try {
      await (video as LocalVideoTrack | null)?.restartTrack({ facingMode: next });
      setFacing(next);
    } catch {
      toast(t('live.no_other_camera'));
    }
  };

  const s = join?.stream;
  const endText =
    endReason === 'reports'
      ? t('live.ended_reports')
      : endReason === 'admin'
        ? t('live.ended_admin')
        : endReason === 'lost'
          ? t('live.ended_lost')
          : t('live.ended');

  return (
    <div className="live-room" data-testid="live-room" data-status={status} data-role={join?.role ?? ''}>
      <video
        ref={videoEl}
        className={`live-video ${streamer && facing === 'user' ? 'mirror' : ''} ${video ? '' : 'hidden'}`}
        autoPlay
        playsInline
        muted
      />
      <div ref={audioHost} hidden />
      {!video && (
        <div className="live-waiting">
          {s && <Avatar name={s.streamer.displayName} photoUrl={s.streamer.photoUrl} size={96} />}
          <span className="muted">
            {status === 'connecting' ? t('live.connecting') : t('live.waiting_video')}
          </span>
        </div>
      )}

      <div className="live-top">
        {s && (
          <span className="live-host">
            <Avatar name={s.streamer.displayName} photoUrl={s.streamer.photoUrl} size={36} />
            <span style={{ minWidth: 0 }}>
              <b className="ellipsis" style={{ display: 'block' }}>
                <PlayerName name={s.streamer.displayName} frame={s.streamer.frame} vip={s.streamer.vip} />
              </b>
              <small className="ellipsis" style={{ display: 'block' }}>
                {s.title}
              </small>
            </span>
          </span>
        )}
        <span className="live-chip" data-testid="live-viewers">
          👁 {viewers}
        </span>
        <span className="live-chip">❤ {likes}</span>
        <button
          className="live-close"
          onClick={() => (streamer ? setConfirmEnd(true) : back())}
          aria-label={streamer ? t('live.end') : 'close'}
          data-testid="live-close"
        >
          ✕
        </button>
      </div>
      {streamer && status === 'live' && <span className="live-on-air">● {t('live.you_are_live')}</span>}
      {needsTap && !streamer && (
        <button
          className="btn btn-primary live-tap"
          onClick={() => {
            void room.current?.startAudio();
            setNeedsTap(false);
          }}
        >
          {t('live.tap_sound')}
        </button>
      )}

      <div className="live-floaters" aria-hidden>
        {floaters.map((f) => (
          <span
            key={f.key}
            className={`live-floater ${f.big ? 'big' : ''}`}
            style={{ left: `${f.big ? 40 : 70 + f.x * 20}%` }}
          >
            <Emoji e={f.emoji} />
          </span>
        ))}
      </div>

      <div className="live-bottom">
        <div className="live-lines" ref={linesEl} data-testid="live-chat">
          {lines.map((l) => (
            <p key={l.id} className={`live-line ${l.gift ? 'gift' : ''}`}>
              {!l.gift && <b className={l.from.vip ? 'vip' : ''}>{l.from.name}</b>} {l.text}
            </p>
          ))}
        </div>
        <div className="live-actions">
          {streamer ? (
            <>
              <button
                className={`live-action ${micOn ? '' : 'off'}`}
                onClick={() => void toggleMic()}
                aria-label="mic"
              >
                {micOn ? '🎙️' : '🔇'}
              </button>
              <button
                className="live-action"
                onClick={() => void switchCamera()}
                aria-label={t('live.camera')}
              >
                🔄
              </button>
            </>
          ) : (
            <>
              <button
                className="live-action"
                onClick={like}
                aria-label={t('live.like')}
                data-testid="live-like"
              >
                ❤️
              </button>
              <button
                className="live-action"
                onClick={() => setGiftOpen(true)}
                aria-label={t('live.gift')}
                data-testid="live-gift"
              >
                🎁
              </button>
              <button
                className="live-action small"
                onClick={() => void report()}
                aria-label={t('live.report')}
              >
                🚩
              </button>
            </>
          )}
        </div>
        <form className="live-input" onSubmit={(e) => void send(e)}>
          <input
            className="input"
            value={text}
            maxLength={200}
            placeholder={t('live.chat_placeholder')}
            onChange={(e) => setText(e.target.value)}
            enterKeyHint="send"
            data-testid="live-input"
          />
          <button
            className="btn btn-primary"
            type="submit"
            disabled={!text.trim()}
            aria-label={t('common.send')}
          >
            ➤
          </button>
        </form>
      </div>

      {(status === 'ended' || status === 'failed' || noWebrtc) && (
        <div className="domino-overlay" data-testid="live-ended">
          <div className="card domino-result">
            <div className="domino-trophy" aria-hidden>
              📺
            </div>
            <b className="subtitle" style={{ margin: 0 }}>
              {noWebrtc ? t('live.no_webrtc') : status === 'failed' ? t('live.connect_failed') : endText}
            </b>
            {status === 'failed' && failure && (
              <small className="muted live-failure" data-testid="live-failure">
                {failure}
              </small>
            )}
            <button className="btn btn-primary btn-block" onClick={back}>
              {t('live.back')}
            </button>
          </div>
        </div>
      )}
      {giftOpen && s && (
        <GiftPicker
          name={s.streamer.displayName}
          onSend={(g) => void gift(g)}
          onClose={() => setGiftOpen(false)}
        />
      )}
      {confirmEnd && (
        <Sheet onClose={() => setConfirmEnd(false)}>
          <p className="subtitle">{t('live.end_confirm')}</p>
          <div className="grid-2">
            <button className="btn" onClick={() => setConfirmEnd(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-danger" onClick={() => void end()} data-testid="live-end">
              {t('live.end')}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
